import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FilePdf, FileDoc, FileText, UploadSimple, Trash, CheckCircle, WarningCircle, Spinner, BookBookmark } from "@phosphor-icons/react";
import { toast } from "sonner";

const ICON_BY_TYPE = { pdf: FilePdf, docx: FileDoc, txt: FileText };
const ACCEPT = ".pdf,.docx,.txt";

function StatusBadge({ status }) {
  if (status === "ready") return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-400" data-testid="kb-status-ready">
      <CheckCircle size={12} weight="fill" /> Listo
    </span>
  );
  if (status === "processing") return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-400" data-testid="kb-status-processing">
      <Spinner size={12} className="animate-spin" /> Procesando
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-red-400" data-testid="kb-status-failed">
      <WarningCircle size={12} weight="fill" /> Falló
    </span>
  );
}

export default function Knowledge() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/knowledge");
      setDocs(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleFile = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "docx", "txt"].includes(ext)) {
      toast.error("Formato no soportado. Solo PDF, DOCX o TXT");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Archivo demasiado grande (max 10MB)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/knowledge/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.file_name} cargado (${data.chunk_count} fragmentos)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir el archivo");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar este documento? La IA dejará de usarlo al instante.")) return;
    try {
      await api.delete(`/knowledge/${id}`);
      toast.success("Documento eliminado");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto fade-in" data-testid="knowledge-page">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <BookBookmark size={14} className="text-[#5E6AD2]" weight="fill" />
          <div className="text-xs uppercase tracking-[0.2em] text-[#71717A]">Base de conocimiento</div>
        </div>
        <h1 className="font-display text-4xl font-medium tracking-tighter">Capacita tu IA</h1>
        <p className="text-sm text-[#A1A1AA] mt-1 max-w-2xl">
          Sube manuales, catálogos o FAQs en PDF, DOCX o TXT. La IA leerá el contenido relevante
          automáticamente cuando un cliente pregunte algo relacionado.
        </p>
      </div>

      {/* Dropzone */}
      <div
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed border-[#27272A] hover:border-[#5E6AD2] bg-[#121214] rounded-lg p-10 text-center transition-colors"
        data-testid="kb-dropzone"
      >
        <UploadSimple size={32} className="text-[#5E6AD2] mx-auto mb-3" />
        <div className="text-base font-medium mb-1">Arrastra un archivo aquí</div>
        <div className="text-xs text-[#71717A] mb-4">o</div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="kb-file-input"
        />
        <Button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="bg-[#5E6AD2] hover:bg-[#4B55C3]"
          data-testid="kb-upload-button"
        >
          {uploading ? "Subiendo..." : "Seleccionar archivo"}
        </Button>
        <div className="text-[10px] uppercase tracking-wider text-[#71717A] mt-4">
          PDF · DOCX · TXT · máx 10 MB
        </div>
      </div>

      {/* Documents list */}
      <div className="mt-10">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-xl font-medium tracking-tight">Documentos cargados</h2>
          <div className="text-xs text-[#71717A]" data-testid="kb-count">{docs.length} {docs.length === 1 ? "archivo" : "archivos"}</div>
        </div>

        {loading ? (
          <div className="text-sm text-[#71717A]">Cargando…</div>
        ) : docs.length === 0 ? (
          <div className="border border-[#27272A] bg-[#121214] rounded-lg p-12 text-center">
            <div className="text-sm text-[#A1A1AA]">Aún no has subido documentos.</div>
            <div className="text-xs text-[#71717A] mt-1">Sube tu primer PDF arriba para capacitar a tu IA.</div>
          </div>
        ) : (
          <div className="space-y-2">
            {docs.map((d) => {
              const Icon = ICON_BY_TYPE[d.file_type] || FileText;
              return (
                <div
                  key={d.id}
                  data-testid={`kb-doc-${d.id}`}
                  className="flex items-center gap-4 px-4 py-3 border border-[#27272A] bg-[#121214] rounded-lg hover:border-[#3F3F46] transition-colors"
                >
                  <div className="h-10 w-10 rounded-md bg-[#0A0A0C] border border-[#27272A] flex items-center justify-center shrink-0">
                    <Icon size={20} className="text-[#5E6AD2]" weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" title={d.file_name}>{d.file_name}</div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <StatusBadge status={d.status} />
                      <span className="text-[10px] text-[#71717A]">
                        {d.chunk_count} fragmento{d.chunk_count === 1 ? "" : "s"}
                      </span>
                      <span className="text-[10px] text-[#71717A]">
                        {(d.size_bytes / 1024).toFixed(1)} KB
                      </span>
                    </div>
                    {d.status === "failed" && d.error && (
                      <div className="text-[10px] text-red-400 mt-1 truncate" title={d.error}>{d.error}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(d.id)}
                    data-testid={`kb-delete-${d.id}`}
                    className="p-2 rounded-md text-[#71717A] hover:text-red-400 hover:bg-[#18181B]"
                    title="Eliminar"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
